import { FC, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ICreateCoachSessionBody,
  SessionObjective,
} from '../../endpoints/coachSessions';
import { microcycleTemplatesEndpoints } from '../../endpoints/microcycleTemplates';
import { useSessionTaxonomy } from '../CoachSessionDetail/useSessionTaxonomy';
import CustomButton from '../../shared/CustomButton/CustomButton';
import CustomDatePicker from '../../shared/CustomDatePicker/CustomDatePicker';
import GenericModalV2 from '../../shared/GenericModal/GenericModal';
import Typhography from '../../shared/Typhography/Typhography';
import { OBJECTIVE_LABELS } from './useCoachSessionWeekView';
import { getSportCapabilities } from '@leopart/sport/sport-capabilities';
import styles from './CreateSessionModal.module.scss';

const toDate = (iso: string): Date | null => {
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const toIsoDate = (d: Date | null): string => {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const toTimeDate = (hhmm: string): Date | null => {
  if (!hhmm) return null;
  const [h, m] = hhmm.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  const d = new Date();
  d.setHours(h, m, 0, 0);
  return d;
};

const toHhmm = (d: Date | null): string => {
  if (!d) return '';
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

interface CreateSessionModalProps {
  teamId: string;
  defaultDate: string;
  isLoading: boolean;
  onConfirm: (body: ICreateCoachSessionBody) => void;
  onClose: () => void;
}

const OBJECTIVES: SessionObjective[] = [
  'tactical',
  'physical',
  'technical',
  'recovery',
  'match_prep',
  'mixed',
];

export const CreateSessionModal: FC<CreateSessionModalProps> = ({
  teamId,
  defaultDate,
  isLoading,
  onConfirm,
  onClose,
}) => {
  const [title, setTitle] = useState('');
  const [date, setDate] = useState(defaultDate);
  const [startTime, setStartTime] = useState('');
  const [objective, setObjective] = useState<SessionObjective>('mixed');
  const [keepersWorkSeparate, setKeepersWorkSeparate] = useState(false);
  const [templateSource, setTemplateSource] = useState<
    'empty' | 'leopart' | 'personal'
  >('empty');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');

  const { sport } = useSessionTaxonomy(teamId);

  const { data: templates = [] } = useQuery({
    queryKey: ['microcycleTemplates', templateSource, sport],
    queryFn: () =>
      microcycleTemplatesEndpoints
        .list({
          scope: templateSource === 'leopart' ? 'SYSTEM' : 'PERSONAL',
          sport: sport ?? undefined,
        })
        .then((r) => r.data),
    enabled: templateSource !== 'empty',
  });

  const selectedTemplate = templates.find((t) => t._id === selectedTemplateId);

  const getSuggestedTitleFromTemplate = (): string => {
    if (!selectedTemplate) return title;
    const dayIdx = new Date(date).getDay();
    const isoIdx = dayIdx === 0 ? 6 : dayIdx - 1;
    const day = selectedTemplate.structure.find((d) => d.dayIndex === isoIdx);
    if (day?.sessionType) return `${day.sessionType} · ${day.mdPosition ?? ''}`.trim();
    return title;
  };

  const handleSubmit = () => {
    const finalTitle = title.trim() || getSuggestedTitleFromTemplate();
    if (!finalTitle) return;
    onConfirm({
      teamId,
      title: finalTitle,
      scheduledDate: date,
      startTime: startTime || undefined,
      objective,
      status: 'draft',
      keepersWorkSeparate,
      ...(selectedTemplateId ? { templateId: selectedTemplateId } : {}),
    });
  };

  const Header = (
    <div className={styles.headerWrap}>
      <div className={styles.headerColorBar} />
      <div className={styles.headerContent}>
        <Typhography type="h3" size="bold-16">
          Nuova sessione
        </Typhography>
        <Typhography type="p" size="medium-12">
          Definisci titolo, data, obiettivo e template di partenza
        </Typhography>
      </div>
    </div>
  );

  const Footer = (
    <div className={styles.footerWrap}>
      <CustomButton color="outline" onClick={onClose}>
        Annulla
      </CustomButton>
      <CustomButton
        color="secondary"
        isLoading={isLoading}
        disabled={!title.trim()}
        onClick={handleSubmit}
      >
        Crea sessione
      </CustomButton>
    </div>
  );

  return (
    <GenericModalV2
      isOpen
      onClose={onClose}
      size="md"
      Header={Header}
      Footer={Footer}
    >
      <div className={styles.body}>
        <div className={styles.bodyInner}>
          <div className={styles.field}>
            <label className={styles.label}>Titolo *</label>
            <input
              className={styles.input}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Es. Allenamento settimanale"
              autoFocus
            />
          </div>

          <div className={styles.row}>
            <div className={`${styles.field} ${styles.dateField}`}>
              <label className={styles.label}>Data</label>
              <CustomDatePicker
                date={toDate(date)}
                onChange={(d) => setDate(toIsoDate(d))}
                placeholder="gg/mm/aaaa"
                dateFormat="dd/MM/yyyy"
                showYearDropdown
              />
            </div>
            <div className={`${styles.field} ${styles.dateField}`}>
              <label className={styles.label}>Ora inizio</label>
              <CustomDatePicker
                date={toTimeDate(startTime)}
                onChange={(d) => setStartTime(toHhmm(d))}
                placeholder="HH:MM"
                dateFormat="HH:mm"
                showTimeSelect
                showTimeSelectOnly
                timeIntervals={15}
                timeCaption="Ora"
              />
            </div>
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Obiettivo</label>
            <div className={styles.objRow}>
              {OBJECTIVES.map((obj) => (
                <button
                  key={obj}
                  type="button"
                  className={`${styles.objBtn} ${
                    objective === obj ? styles.objBtnActive : ''
                  }`}
                  onClick={() => setObjective(obj)}
                >
                  {OBJECTIVE_LABELS[obj]}
                </button>
              ))}
            </div>
          </div>

          {getSportCapabilities(sport).hasGoalkeepers && (
            <div className={styles.field}>
              <label
                className={styles.keepersToggle}
                onClick={() => setKeepersWorkSeparate((v) => !v)}
              >
                <input
                  type="checkbox"
                  checked={keepersWorkSeparate}
                  onChange={() => setKeepersWorkSeparate((v) => !v)}
                />
                <span>Lavoro portieri separato</span>
                <span className={styles.keepersHint}>
                  Portieri in track parallelo dedicato
                </span>
              </label>
            </div>
          )}

          <div className={styles.field}>
            <label className={styles.label}>Parti da</label>
            <div className={styles.templateSourceRow}>
              {(['empty', 'leopart', 'personal'] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  className={`${styles.templateSourceBtn} ${
                    templateSource === s ? styles.templateSourceBtnActive : ''
                  }`}
                  onClick={() => {
                    setTemplateSource(s);
                    setSelectedTemplateId('');
                  }}
                >
                  {s === 'empty'
                    ? 'Vuoto'
                    : s === 'leopart'
                      ? 'Template LEOPART'
                      : 'Template personali'}
                </button>
              ))}
            </div>
            {templateSource !== 'empty' && templates.length > 0 && (
              <select
                className={styles.input}
                style={{ marginTop: 6 }}
                value={selectedTemplateId}
                onChange={(e) => setSelectedTemplateId(e.target.value)}
              >
                <option value="">— Seleziona template —</option>
                {templates.map((t) => (
                  <option key={t._id} value={t._id}>
                    {t.name} ({t.structure.length}gg)
                  </option>
                ))}
              </select>
            )}
            {templateSource !== 'empty' && templates.length === 0 && (
              <div
                style={{
                  marginTop: 6,
                  fontSize: 11,
                  color: '#888',
                  fontStyle: 'italic',
                }}
              >
                Nessun template disponibile per questo sport/scope.
              </div>
            )}
          </div>
        </div>
      </div>
    </GenericModalV2>
  );
};
